"use client";
import React, { useEffect, useMemo, useState } from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  AnimatePresence,
} from "framer-motion";
import { ChevronRight, ShoppingCart, X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router";

export default function SwipeToAction({
  addToCart,
  removeFromCart,
  isInCart,
  toggleOverlay,
  userDetail,
  isBottomSheetOpen,
  isSwipeEnabled = true,
}) {
  const router = useNavigate();
  const [added, setAdded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // ✅ Motion values
  const x = useMotionValue(0);
  const trackWidth = 380;
  const thumbWidth = 60;
  const halfway = (trackWidth - thumbWidth) / 2;
  // Progress for swipe
  const progress = useTransform(x, [0, trackWidth - thumbWidth], [0, 1]);

  // NEW: Gradient strength (0 = faded, 1 = full)
  const gradientStrength = useTransform(
    progress,
    [0.5, 1],
    [0.7, 1]            // fade → strong
  );

  // NEW: Conditional faded gradient when swipe disabled
  const bgGradient = useTransform(gradientStrength, (value) =>
    isSwipeEnabled
      ? `linear-gradient(90deg, rgba(37,99,235,${value}) 0%, rgba(16,185,129,${value}) 50%)`
      : `linear-gradient(90deg, rgba(37,99,235,0.2) 0%, rgba(16,185,129,0.2) 100%)`
  );


  // ✅ Sync with Redux / store
  const inCart = useMemo(() => {
    try {
      return typeof isInCart === "function" ? !!isInCart() : !!isInCart;
    } catch (e) {
      console.error("isInCart() threw:", e);
      return false;
    }
  }, [isInCart]);

  useEffect(() => {
    setAdded(inCart);
  }, [inCart]);

  // ✅ Reset swipe when variant or state changes
  useEffect(() => {
    animate(x, 0, { type: "spring", stiffness: 300 });
  }, [isSwipeEnabled, inCart]);

  const handleAdd = async () => {
    if (loading) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      if (!userDetail?.UserID) {
        toggleOverlay?.();
        setLoading(false);
        animate(x, 0, { type: "spring", stiffness: 300 });
        return;
      }
      const res = addToCart?.();
      if (res && typeof res.then === "function") await res;
      setAdded(true);
      animate(x, trackWidth - thumbWidth, { type: "spring", stiffness: 200 });
    } catch (err) {
      console.error("addToCart error:", err);
      setErrorMsg("Could not add to cart. Try again.");
      animate(x, 0, { type: "spring", stiffness: 300 });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (loading) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = removeFromCart?.();
      if (res && typeof res.then === "function") await res;
      setAdded(false);
      animate(x, 0, { type: "spring", stiffness: 250 });
    } catch (err) {
      console.error("removeFromCart error:", err);
      setErrorMsg("Could not remove from cart. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = () => {
    if (!isSwipeEnabled || loading) {
      animate(x, 0, { type: "spring", stiffness: 300 });
      return;
    }

    const currentX = x.get();

    if (currentX > halfway) {
      animate(x, trackWidth - thumbWidth, { type: "spring", stiffness: 200 });

      setTimeout(() => {
        if (isBottomSheetOpen === "buy") {
          handleBuyNowSwipe();
        } else {
          handleAdd();
        }
      }, 180);
    } else {
      animate(x, 0, { type: "spring", stiffness: 300 });
    }
  };


  const handleCheckout = () => {
    if (loading) return;
    try {
      router("/carts");
    } catch {
      if (typeof window !== "undefined") window.location.href = "/carts";
    }
  };

  const handleBuyNowSwipe = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const res = addToCart?.();
      if (res && typeof res.then === "function") await res;

      router("/carts");
    } catch (err) {
      console.error("Buy Now Swipe Error:", err);
      setErrorMsg("Could not continue. Try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="my-4 flex flex-col items-center justify-center w-full relative">
      {/* ✅ Wrap AnimatePresence in absolute-positioned container */}
      <div className="relative w-full flex items-center justify-center min-h-[60px]">
        <AnimatePresence mode="wait">
          {!added ? (
            // ✅ Swipe Section
            <motion.div
              key="swipe"
              layout
              initial={{ opacity: 0, y: 10, position: "absolute" }}
              animate={{ opacity: 1, y: 0, position: "relative" }}
              exit={{ opacity: 0, y: -10, position: "absolute" }}
              transition={{ type: "spring", stiffness: 120, damping: 14 }}
              className={`relative w-[380px] h-[60px] rounded-full backdrop-blur-lg border border-white-1/10  overflow-hidden ${(!isSwipeEnabled || loading) ? "cursor-not-allowed opacity-70" : ""
                }`}
              style={{ background: bgGradient }}
            >
              {/* Text */}
              <div className="absolute ml-5 inset-0 flex flex-col items-center justify-center text-center text-white-1 font-semibold tracking-wide pointer-events-none">
                <span className="text-[14px] uppercase tracking-wider">
                  {isSwipeEnabled
                    ? isBottomSheetOpen === "buy"
                      ? "Swipe to Checkout"
                      : "Swipe to Add to Cart"
                    : "Select size, color & quantity"}

                </span>
              </div>

              {/* Error */}
              {errorMsg && (
                <div className="absolute bottom-1 left-3 right-3 text-xs text-red-600 text-center">
                  {errorMsg}
                </div>
              )}

              {/* Thumb */}
              <motion.div
                drag={(!isSwipeEnabled || loading) ? false : "x"}
                dragConstraints={{ left: 0, right: trackWidth - thumbWidth }}
                style={{ x }}
                onDragEnd={handleDragEnd}
                whileTap={{ scale: (!isSwipeEnabled || loading) ? 1 : 0.95 }}
                className={`absolute top-[5px] left-[5px] w-[50px] h-[50px] ${(!isSwipeEnabled || loading) ? "bg-gray-200" : "bg-white-1"
                  } rounded-full flex items-center justify-center shadow-md select-none`}
              >
                <ChevronRight
                  color={(!isSwipeEnabled || loading) ? "#9CA3AF" : "#16A34A"}
                  size={28}
                />
              </motion.div>
            </motion.div>
          ) : (
            // ✅ Remove + Checkout Section
            <motion.div
              key="actions"
              layout
              initial={{ opacity: 0, y: 15, position: "absolute" }}
              animate={{ opacity: 1, y: 0, position: "relative" }}
              exit={{ opacity: 0, y: -10, position: "absolute" }}
              transition={{ type: "spring", stiffness: 120, damping: 14 }}
              className="flex items-center justify-between w-[380px] gap-3 mt-1"
            >
              {inCart ? (
                <motion.button
                  whileTap={{ scale: 0.96 }}
                  onClick={handleRemove}
                  disabled={loading}
                  className="flex-1 h-[52px] flex items-center justify-center gap-2 rounded-full border border-peach text-peach font-semibold hover:bg-red-50 transition-all duration-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
                >
                  <X size={18} />
                  {loading ? "Removing..." : "Remove"}
                </motion.button>
              ) : (
                <div className="flex-1 h-[52px] flex items-center justify-center gap-2 rounded-full border border-gray-200 text-gray-400 bg-white-1/80 text-sm">
                  Not in cart
                </div>
              )}

              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={handleCheckout}
                disabled={loading}
                className="flex-1 h-[52px] flex items-center justify-center gap-2 rounded-full bg-blue text-white-1 font-semibold hover:shadow-lg transition-all duration-200"
              >
                <ShoppingCart size={20} />
                Checkout
                <ArrowRight size={18} className="ml-1" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
